import React, { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Grid, MenuItem, Typography } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { Container } from './components/Container';
import Card from './components/Card';
import AutoCompleteContext from 'components/CustomInputsInFormikContext/AutoComplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SelectFieldContext from 'components/CustomInputsInFormikContext/Select';
import Progress from 'components/Progress';
import * as Yup from 'yup';
import api from 'services/api';
import PondService from 'services/PondService';
import { useSelectors } from 'hooks/useSelectors';
import { displayCycleInfoDashboard } from 'utils';
import { HtmlTooltip } from 'components/tooltips/HtmlTootip';
import InfoIcon from '@mui/icons-material/Info';
import ReactToPrint from 'react-to-print';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function ReportInput() {
  const formik = useFormik({
    initialValues: {
      selectedPonds: [],
      selectedCycles: [],
      type: 'table',
    },
    validationSchema: Yup.object({
      selectedPonds: Yup.array()
        .required('Campo Obrigatório')
        .test(
          'is-non-empty',
          'Campo obrigatório',
          (array) => array && array.length > 0
        ),
      selectedCycles: Yup.array()
        .required('Campo Obrigatório')
        .test(
          'is-non-empty',
          'Campo obrigatório',
          (array) => array && array.length > 0
        ),
    }),
  });

  const { type, selectedCycles, selectedPonds } = formik.values;

  const day = new Date().getDate();
  const month = new Date().getMonth() + 1;
  const year = new Date().getFullYear();

  const [ponds, setPonds] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [exportPdf, setExportPdf] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const componentRef = useRef();
  const promiseResolveRef = useRef(null);

  const { cycles } = useSelectors();

  const handleExportPdf = () => {
    setExportPdf((prevState) => !prevState);
  };

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      const pondIds = JSON.stringify(selectedPonds.map((item) => item.id));
      const cycleIds = JSON.stringify(selectedCycles.map((item) => item.id));

      try {
        const response = (
          await api.get('/reports/input', {
            params: {
              cycle_code_id: cycleIds,
              ponds_id: pondIds,
            },
          })
        ).data;
        setReportData(response);
      } catch (error) {
        throw new Error(error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [selectedCycles, selectedPonds]);

  useEffect(() => {
    async function loadPonds() {
      setIsLoading(true);
      try {
        const response = await PondService.pondList();
        setPonds(response);
      } catch (error) {
        throw new Error(error);
      } finally {
        setIsLoading(false);
      }
    }
    loadPonds();
  }, []);

  return (
    <FormikProvider value={formik}>
      {isLoading && <Progress />}
      <Container p={2}>
        <Grid container spacing={4}>
          <Grid
            item
            xs
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontSize={22}>
              Relatório de Consumo de Insumos{' '}
              <HtmlTooltip
                title="Esse relatório tem como objetivo acompanhar o consumo e custo dos insumos de cada viveiro na fazenda durante um período de tempo."
                placement="top"
                arrow
              >
                <InfoIcon color="primary" fontSize="inherit" />
              </HtmlTooltip>
            </Typography>
            <ReactToPrint
              trigger={() => (
                <Button
                  variant="outlined"
                  sx={{
                    marginRight: '16px',
                    borderRadius: '8px',
                    textTransform: 'capitalize',
                    color: '#404944',
                    borderColor: '#404944',
                    ':hover': '#000',
                  }}
                >
                  Exportar todos
                </Button>
              )}
              content={() => componentRef.current}
              onBeforeGetContent={() => {
                return new Promise((resolve) => {
                  handleExportPdf();
                  promiseResolveRef.current = resolve;
                  setIsPrinting(true);
                });
              }}
              onAfterPrint={() => {
                promiseResolveRef.current = null;
                setIsPrinting(false);
                handleExportPdf();
              }}
              documentTitle={`GREENFY-Relatorio de produção-${day}-${month}-${year}`}
            />
          </Grid>
        </Grid>
      </Container>
      <Container p={4}>
        <Grid container spacing={2}>
          <Grid item xs>
            <AutoCompleteContext
              disableCloseOnSelect
              multiple
              limitTags={2}
              label="Viveiros"
              name="selectedPonds"
              options={ponds}
              getOptionLabel={(opt) => opt.description}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.description}
                </li>
              )}
            />
          </Grid>

          <Grid item xs>
            <AutoCompleteContext
              disableCloseOnSelect
              multiple
              limitTags={2}
              label="Ciclo Produtivo"
              name="selectedCycles"
              options={cycles}
              getOptionLabel={(opt) =>
                displayCycleInfoDashboard({
                  cycleBegin: opt.start,
                  cycleCod: opt.cod,
                  cycleEnd: opt.end,
                })
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {displayCycleInfoDashboard({
                    cycleBegin: option.start,
                    cycleCod: option.cod,
                    cycleEnd: option.end,
                  })}
                </li>
              )}
            />
          </Grid>

          <Grid item xs>
            <SelectFieldContext label="Tipo" name="type">
              <MenuItem key={1} value="graphic">
                Gráfico
              </MenuItem>
              <MenuItem key={2} value="table">
                Tabela
              </MenuItem>
            </SelectFieldContext>
          </Grid>
        </Grid>
      </Container>

      <Container noBgWrapper ref={componentRef}>
        <Grid container spacing={2} pb={6}>
          {reportData?.length === 0 && (
            <Grid item xs>
              Ajuste os filtros acima para visualizar os dados dos viveiros.
            </Grid>
          )}
          {reportData?.map(({ graphic, table }) => (
            <>
              {type.includes('graphic') && (
                <>
                  {graphic.ponds.map((pond) => (
                    <Grid
                      item
                      lg={12}
                      style={{
                        pageBreakInside: 'avoid',
                        pageBreakBefore: 'auto',
                        pageBreakAfter: 'auto',
                      }}
                    >
                      <Card
                        title={`${pond.name} - ${displayCycleInfoDashboard({
                          cycleBegin: pond.start,
                          cycleCod: pond.cycleCod,
                          cycleEnd: pond.end,
                        })}`}
                        data={{
                          ...pond,
                          years: graphic?.years,
                          months: graphic?.months,
                        }}
                        type={type}
                        exportPdf={exportPdf}
                      />
                    </Grid>
                  ))}
                </>
              )}
              {type.includes('table') && (
                <>
                  {table.map((pond) => (
                    <Grid
                      item
                      xs
                      lg={6}
                      style={{
                        pageBreakInside: 'avoid',
                        pageBreakBefore: 'auto',
                        pageBreakAfter: 'auto',
                      }}
                    >
                      <Card
                        title={`${pond.name} - ${displayCycleInfoDashboard({
                          cycleBegin: pond.start,
                          cycleCod: pond.cycleCod,
                          cycleEnd: pond.end,
                        })}`}
                        data={pond}
                        type={type}
                        exportPdf={exportPdf}
                      />
                    </Grid>
                  ))}
                </>
              )}
            </>
          ))}
        </Grid>
      </Container>
    </FormikProvider>
  );
}

export default ReportInput;
